import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Mail, Send, Users, User, Search, CheckCircle2, Clock,
  AlertTriangle, ChevronDown, ChevronUp, Eye, RefreshCw,
  Building2, X, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_ROLES = ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'];

export default function EmailBroadcast() {
  const { user } = useAuth();
  // Pour les super_admin, on utilise le sélecteur d'entreprise du contexte
  // Pour les autres rôles, on utilise directement le company_id du token
  const { selectedCompanyId } = useCompanyScope();

  // Form state
  const [recipientType, setRecipientType] = useState('all'); // 'all' | 'selected'
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employees, setEmployees] = useState([]);
  const [emailCount, setEmailCount] = useState(0); // nombre d'employés avec email valide
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [sending, setSending] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailData, setDetailData] = useState({});

  // Preview
  const [showPreview, setShowPreview] = useState(false);

  const token = localStorage.getItem('token');

  const isAllowed = ALLOWED_ROLES.includes(user?.role);

  const CROSS_COMPANY_ROLES = ['super_admin', 'group_admin', 'group_hr'];
  const isCrossCompany = CROSS_COMPANY_ROLES.includes(user?.role);
  // Les rôles cross-company n'ont pas de company_id dans le token → ils utilisent le sélecteur
  const effectiveCompanyId = isCrossCompany ? selectedCompanyId : user?.company_id;

  useEffect(() => {
    if (isAllowed) {
      fetchEmployees();
      fetchHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId, isAllowed]);

  const fetchEmployees = async () => {
    setLoadingEmps(true);
    try {
      const params = {};
      // Les rôles cross-company envoient le company_id en paramètre
      // Les rôles liés à une entreprise ont leur company_id dans le token JWT
      if (isCrossCompany && effectiveCompanyId) {
        params.company_id = effectiveCompanyId;
      } else if (isCrossCompany && !effectiveCompanyId) {
        // Pas d'entreprise sélectionnée → on ne charge pas
        setEmployees([]);
        setEmailCount(0);
        setLoadingEmps(false);
        return;
      }
      console.log('[EmailBroadcast] fetchEmployees params:', params, 'role:', user?.role, 'company_id:', user?.company_id);
      const res = await api.get('/broadcast/employees', { params });
      console.log('[EmailBroadcast] employees response:', res.data?.length, 'items', res.data?.slice(0,3));
      const list = res.data || [];
      setEmployees(list);
      setEmailCount(list.filter(e => e.has_email).length);
    } catch (e) {
      console.error('[EmailBroadcast] Erreur chargement employés:', e.response?.data || e.message);
      setEmployees([]);
      setEmailCount(0);
    } finally {
      setLoadingEmps(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/broadcast/history');
      setHistory(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchDetail = async (id) => {
    if (detailData[id]) { setExpandedId(expandedId === id ? null : id); return; }
    try {
      const res = await api.get(`/broadcast/history/${id}`);
      setDetailData(prev => ({ ...prev, [id]: res.data }));
      setExpandedId(id);
    } catch (e) {
      toast.error('Impossible de charger les détails.');
    }
  };

  const toggleEmployee = (emp) => {
    setSelectedEmployees(prev =>
      prev.find(e => e.id === emp.id)
        ? prev.filter(e => e.id !== emp.id)
        : [...prev, emp]
    );
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Le sujet est obligatoire.'); return; }
    if (!bodyHtml.trim()) { toast.error('Le message est obligatoire.'); return; }
    if (recipientType === 'selected' && selectedEmployees.length === 0) {
      toast.error('Sélectionnez au moins un destinataire.');
      return;
    }

    const confirm = window.confirm(
      recipientType === 'all'
        ? `Envoyer cet email à TOUS les employés de l'entreprise ?`
        : `Envoyer cet email à ${selectedEmployees.length} destinataire(s) sélectionné(s) ?`
    );
    if (!confirm) return;

    setSending(true);
    try {
      const payload = {
        subject,
        body_html: bodyHtml,
        recipient_type: recipientType,
        employee_ids: recipientType === 'selected' ? selectedEmployees.map(e => e.id) : [],
      };
      if (isCrossCompany && effectiveCompanyId) {
        payload.target_company_id = effectiveCompanyId;
      }
      const res = await api.post('/broadcast/send', payload);
      toast.success(`✅ ${res.data.message}`);
      setSubject('');
      setBodyHtml('');
      setSelectedEmployees([]);
      setTimeout(fetchHistory, 2000); // refresh après envoi
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi.');
    } finally {
      setSending(false);
    }
  };

  const filteredEmployees = employees.filter(e => {
    const search = employeeSearch.toLowerCase().trim();
    if (!search) return true;
    return (
      (e.name || '').toLowerCase().includes(search) ||
      (e.email || '').toLowerCase().includes(search) ||
      (e.matricule || '').toLowerCase().includes(search) ||
      (e.position || '').toLowerCase().includes(search)
    );
  });

  const statusColor = (status) => {
    if (status === 'sent') return 'bg-green-100 text-green-700 border-green-300';
    if (status === 'partial') return 'bg-amber-100 text-amber-700 border-amber-300';
    if (status === 'failed') return 'bg-red-100 text-red-700 border-red-300';
    return 'bg-blue-100 text-blue-700 border-blue-300';
  };

  const statusLabel = (status) => {
    if (status === 'sent') return '✅ Envoyé';
    if (status === 'partial') return '⚠️ Partiel';
    if (status === 'failed') return '❌ Échoué';
    return '⏳ En cours';
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso; }
  };

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-slate-500">Accès non autorisé.</p>
      </div>
    );
  }

  // Rôle cross-company sans entreprise sélectionnée
  if (isCrossCompany && !effectiveCompanyId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
            <Mail className="w-8 h-8 text-primary" />
            Emails groupés
          </h1>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[300px] bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <Building2 className="w-12 h-12 text-amber-400 mb-4" />
          <h3 className="text-lg font-semibold text-amber-800 mb-2">Sélectionnez une entreprise</h3>
          <p className="text-amber-700 text-sm max-w-sm">
            Utilisez le sélecteur d'entreprise en haut de la page pour choisir l'entreprise dont vous souhaitez contacter les employés.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
          <Mail className="w-8 h-8 text-primary" />
          Emails groupés
        </h1>
        <p className="text-slate-500 mt-1">
          Envoyez des communications sans réponse à vos collaborateurs. Expéditeur : <span className="font-mono text-slate-700">noreply@lxs-group.com</span>
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ── Formulaire de composition ── */}
        <div className="xl:col-span-2 space-y-5">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Send className="w-4 h-4 text-primary" />
                Composer un email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Type de destinataires */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Destinataires</Label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setRecipientType('all')}
                    className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      recipientType === 'all'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Tous les employés
                    <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {loadingEmps ? '...' : `${emailCount} avec email`}
                    </span>
                  </button>
                  <button
                    onClick={() => setRecipientType('selected')}
                    className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      recipientType === 'selected'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    Sélection manuelle
                    {selectedEmployees.length > 0 && (
                      <span className="ml-auto text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                        {selectedEmployees.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Sélecteur d'employés */}
              {recipientType === 'selected' && (
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Sélectionner les destinataires
                  </Label>
                  {/* Tags sélectionnés */}
                  {selectedEmployees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedEmployees.map(e => (
                        <span
                          key={e.id}
                          className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2.5 py-1 rounded-full"
                        >
                          {e.name}
                          <button onClick={() => toggleEmployee(e)} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Recherche */}
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Rechercher par nom, email ou matricule..."
                      value={employeeSearch}
                      onChange={e => setEmployeeSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="border border-slate-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-slate-100">
                    {loadingEmps ? (
                      <div className="flex justify-center py-6"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
                    ) : filteredEmployees.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-6">Aucun employé trouvé</p>
                    ) : filteredEmployees.map(emp => {
                      const isSelected = selectedEmployees.find(e => e.id === emp.id);
                      return (
                        <div
                          key={emp.id}
                          onClick={() => emp.has_email && toggleEmployee(emp)}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                            !emp.has_email ? 'opacity-40 cursor-not-allowed' :
                            isSelected ? 'bg-primary/5' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                            isSelected ? 'bg-primary border-primary' : 'border-slate-300'
                          }`}>
                            {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{emp.name}</p>
                            <p className="text-xs text-slate-500 truncate">{emp.email || 'Pas d\'email'} · {emp.matricule}</p>
                          </div>
                          <span className="text-xs text-slate-400 shrink-0">{emp.position}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Les employés sans adresse email sont grisés et ne peuvent pas être sélectionnés.
                  </p>
                </div>
              )}

              {/* Sujet */}
              <div>
                <Label htmlFor="email-subject" className="text-sm font-medium text-slate-700">
                  Sujet <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email-subject"
                  placeholder="Ex: Réunion d'équipe — Lundi 1er septembre"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Corps du message */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="email-body" className="text-sm font-medium text-slate-700">
                    Message <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <span className="text-xs text-slate-400">Balises : <code className="bg-slate-100 px-1 rounded">{'{{prenom}}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{{nom}}'}</code></span>
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      {showPreview ? 'Masquer' : 'Aperçu'}
                    </button>
                  </div>
                </div>
                <textarea
                  id="email-body"
                  rows={10}
                  placeholder={`Bonjour {{prenom}},\n\nNous vous informons que...\n\nCordialement,\nL'équipe RH`}
                  value={bodyHtml}
                  onChange={e => setBodyHtml(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                />
                {showPreview && bodyHtml && (
                  <div className="mt-3 border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-2 font-medium">Aperçu (avec données exemple)</p>
                    <div
                      className="text-sm prose max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: bodyHtml
                          .replace(/{{prenom}}/g, 'Jean')
                          .replace(/{{nom}}/g, 'Jean Dupont')
                          .replace(/\n/g, '<br/>')
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Info noreply */}
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  L'email sera envoyé depuis <strong>noreply@lxs-group.com</strong>. Les destinataires ne peuvent pas répondre. Votre nom (<strong>{user?.full_name || user?.email}</strong>) sera tracé dans l'historique.
                </p>
              </div>

              {/* Bouton envoi */}
              <Button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !bodyHtml.trim()}
                className="w-full gap-2"
                size="lg"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Envoi en cours...' : (
                  recipientType === 'all'
                    ? `Envoyer à tous (${emailCount} emails)`
                    : `Envoyer à ${selectedEmployees.length} destinataire(s)`
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ── Historique ── */}
        <div className="xl:col-span-1">
          <Card className="h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="w-4 h-4 text-slate-500" />
                  Historique des envois
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchHistory} disabled={loadingHistory}>
                  <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingHistory ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : history.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <Mail className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Aucun email envoyé</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                  {history.map(item => (
                    <div key={item.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate" title={item.subject}>
                            {item.subject}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${statusColor(item.status)}`}>
                              {statusLabel(item.status)}
                            </span>
                            <span className="text-xs text-slate-500">
                              {item.recipient_count} dest.
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">
                            Par <span className="text-slate-600 font-medium">{item.sender_name}</span>
                          </p>
                          <p className="text-xs text-slate-400">{formatDate(item.sent_at)}</p>
                        </div>
                        <button
                          onClick={() => fetchDetail(item.id)}
                          className="shrink-0 text-slate-400 hover:text-primary"
                          title="Voir les détails"
                        >
                          {expandedId === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* Détail déplié */}
                      {expandedId === item.id && detailData[item.id] && (
                        <div className="mt-3 bg-slate-50 rounded-lg p-3 text-xs space-y-2">
                          <div className="grid grid-cols-2 gap-1">
                            <span className="text-slate-500">Succès</span>
                            <span className="text-green-600 font-medium">{detailData[item.id].success_count}</span>
                            <span className="text-slate-500">Échecs</span>
                            <span className="text-red-600 font-medium">{detailData[item.id].fail_count}</span>
                          </div>
                          {detailData[item.id].fail_emails?.length > 0 && (
                            <div>
                              <p className="text-slate-500 mb-1">Emails en échec :</p>
                              {detailData[item.id].fail_emails.map(e => (
                                <p key={e} className="text-red-600 font-mono">{e}</p>
                              ))}
                            </div>
                          )}
                          <div>
                            <p className="text-slate-500 mb-1">Destinataires ({detailData[item.id].recipients?.length}) :</p>
                            <div className="max-h-24 overflow-y-auto space-y-0.5">
                              {detailData[item.id].recipients?.map(r => (
                                <p key={r.id} className="text-slate-600 truncate">{r.name} — {r.email}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
